import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { apiURL } from "../config/config";
import { format } from "date-fns"; // ใช้ date-fns ช่วยจัดรูปแบบวันที่
import quotationTypes from "../data/quotationTypes.json";

const CreateQuotationForm = ({
  quotationData,
  setQuotationData,
  handleChange,
  handleClientChange,
}) => {
  const [clients, setClients] = useState([]); // state สำหรับเก็บข้อมูลลูกค้า
  const [loadingClients, setLoadingClients] = useState(false); // state สำหรับโหลดข้อมูลลูกค้า
  const [availableTypes, setAvailableTypes] = useState([]);

  // ดึงข้อมูลลูกค้าจาก API
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await axios.get(`${apiURL}clients`);
        setClients(response.data); // บันทึกข้อมูลลูกค้าใน state
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    // ✅ ถ้ามีค่า createBy (หมายถึงมาจาก API) ไม่ต้อง Override
    if (quotationData.createBy && quotationData.proposedBy) {
      return;
    }
  
    // ✅ ดึงข้อมูล user จาก localStorage
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const email = user.username || "";
    const domain = email.split("@")[1]?.split(".")[0] || "";
    const companyName =
      domain === "neonworks" ? "NEON" : domain === "optx" ? "OPTX" : "UNKNOWN";
  
    // ✅ ตั้งค่า Create By และ Proposed By ตามบริษัท เฉพาะตอนสร้างใหม่เท่านั้น
    if (companyName === "OPTX") {
      setQuotationData((prev) => ({
        ...prev,
        createBy: "CHAWACHAI WICHAIDIT",
        proposedBy: "CHAWACHAI WICHAIDIT",
      }));
    } else {
      setQuotationData((prev) => ({
        ...prev,
        createBy: "NATORN MANEEKORN",
        proposedBy: "AKEDANAI YUKTANAN",
      }));
    }
  }, [quotationData.createBy, quotationData.proposedBy, setQuotationData]);  

  // ดึงประเภทงานของบริษัทจาก JSON
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    const company = (user.company || "").toUpperCase(); // ✅ แปลงเป็นตัวพิมพ์ใหญ่ให้ตรงกับ JSON
    if (company && quotationTypes.companies[company]) {
      setAvailableTypes(
        Object.entries(quotationTypes.companies[company]).map(
          ([key, value]) => ({
            label: key, // "Creative"
            value: value, // "C"
          })
        )
      );
    } else {
      setAvailableTypes([]);
    }
  }, []);

  // ✅ ฟังก์ชันช่วยแปลงวันที่เป็น format ที่ต้องการ
  const formatPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return ""; // ถ้าไม่มีค่า ให้ return ค่าว่าง

    const start = new Date(startDate);
    const end = new Date(endDate);

    // ตรวจสอบว่าอยู่ในเดือนเดียวกันหรือไม่
    const sameMonth =
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();

    if (sameMonth) {
      return format(start, "MMM yyyy"); // Ex: "Jan 2024"
    } else {
      return `${format(start, "dd MMM yyyy")} - ${format(end, "dd MMM yyyy")}`; // Ex: "15 Jan 2024 - 30 Feb 2024"
    }
  };

  // ห่อ setQuotationData ด้วย useCallback
  const updateQuotationData = useCallback(
    (newData) => {
      setQuotationData((prev) => ({ ...prev, ...newData }));
    },
    [setQuotationData]
  );

  // ✅ ใช้ใน useEffect
  useEffect(() => {
    updateQuotationData({
      period: formatPeriod(quotationData.startDate, quotationData.endDate),
    });
  }, [quotationData.startDate, quotationData.endDate, updateQuotationData]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {/* Title */}
      <div>
        <label className="block mb-1 text-gray-600">Title</label>
        <input
          type="text"
          name="title"
          placeholder="Enter title"
          value={quotationData.title}
          onChange={handleChange}
          className="w-full px-4 py-2 border rounded"
          required
        />
      </div>

      {/* Client */}
      <div>
        <label className="block mb-1 text-gray-600">Client</label>
        {loadingClients ? (
          <p>Loading clients...</p>
        ) : (
          <select
            value={quotationData.clientId || quotationData.client}
            onChange={(e) =>
              handleClientChange(
                e.target.value,
                e.target.options[e.target.selectedIndex].text
              )
            }
            className="w-full px-4 py-2 border rounded"
            required
          >
            <option value="">Select Client</option>
            {clients.map((client) => (
              <option key={client._id} value={client._id}>
                {client.customerName}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Create By (แทน Sale Person) */}
      <div>
        <label className="block mb-1 text-gray-600">Create By</label>
        <input
          type="text"
          name="salePerson" // ยังใช้ name เดิมเพื่อให้โค้ดยังทำงานได้ปกติ
          placeholder="Enter creator name"
          value={quotationData.salePerson}
          onChange={handleChange}
          className="w-full px-4 py-2 border rounded"
          required
        />
      </div>

      {/* Document Date */}
      <div>
        <label className="block mb-1 text-gray-600">Document Date</label>
        <input
          type="date"
          name="documentDate"
          value={quotationData.documentDate}
          onChange={handleChange}
          className="w-full px-4 py-2 border rounded"
          required
        />
      </div>

      {/* Product Name */}
      <div>
        <label className="block mb-1 text-gray-600">Product Name</label>
        <input
          type="text"
          name="productName"
          placeholder="Enter product name"
          value={quotationData.productName}
          onChange={handleChange}
          className="w-full px-4 py-2 border rounded"
          required
        />
      </div>

      {/* Project Name */}
      <div>
        <label className="block mb-1 text-gray-600">Project Name</label>
        <input
          type="text"
          name="projectName"
          placeholder="Enter project name"
          value={quotationData.projectName}
          onChange={handleChange}
          className="w-full px-4 py-2 border rounded"
          required
        />
      </div>

      {/* Period (Disabled) */}
      <div>
        <label className="block mb-1 text-gray-600">Period</label>
        <input
          type="text"
          name="period"
          value={quotationData.period}
          className="w-full px-4 py-2 border rounded bg-gray-100"
          disabled
        />
      </div>

      {/* Start Date and End Date */}
      <div className="md:col-span-2 flex gap-4">
        <div className="flex-1">
          <label className="block mb-1 text-gray-600">Start Date</label>
          <input
            type="date"
            name="startDate"
            value={quotationData.startDate}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
            required
          />
        </div>
        <div className="flex-1">
          <label className="block mb-1 text-gray-600">End Date</label>
          <input
            type="date"
            name="endDate"
            value={quotationData.endDate}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
            required
          />
        </div>
      </div>

      {/* Type and Fee */}
      <div className="md:col-span-2 flex gap-4">
        {/* Type Selection */}
        <div className="flex-1">
          <label className="block mb-1 text-gray-600">Type</label>
          <select
            name="type"
            value={quotationData.type || ""}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
            required
          >
            <option value="">Select Type</option>
            {availableTypes.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block mb-1 text-gray-600">Fee</label>
          <div className="relative">
            <input
              type="number"
              name="fee"
              placeholder="Enter fee"
              value={quotationData.fee}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setQuotationData({
                  ...quotationData,
                  fee: value < 0 ? 0 : value, // ห้ามค่าติดลบ
                });
              }}
              className="w-full px-4 py-2 border rounded pr-10"
              required
            />
            <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">
              %
            </span>
          </div>
        </div>
      </div>
      {/* Create By (แทน Proposed By) */}
      <div className="md:col-span-2 flex gap-4">
        <div className="flex-1">
          <label className="block mb-1 text-gray-600">Proposed By</label>
          <input
            type="text"
            name="createBy"
            placeholder="Enter proposed name"
            value={quotationData.createBy}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
            required
            readOnly
          />
        </div>
        {/* Approved By (แทน Proposed By) */}
        <div className="flex-1">
          <label className="block mb-1 text-gray-600">Approved By</label>
          <input
            type="text"
            name="proposedBy"
            placeholder="Enter approved name"
            value={quotationData.proposedBy}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
            required
            readOnly
          />
        </div>
      </div>
      {/* Discount, CreditTerm, and Remark */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Discount */}
        <div>
          <label className="block mb-1 text-gray-600">Discount</label>
          <input
            type="number"
            name="discount"
            placeholder="Enter discount"
            value={quotationData.discount || ""}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
          />
        </div>

        {/* CreditTerm */}
        {/* <div>
          <label className="block mb-1 text-gray-600">Credit Term</label>
          <input
            type="number"
            name="CreditTerm"
            placeholder="Enter credit term (days)"
            value={quotationData.CreditTerm || ""}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
            required
          />
        </div> */}

        {/* Credit Term (Dropdown) */}
        <div>
          <label className="block mb-1 text-gray-600">Credit Term</label>
          <select
            name="CreditTerm"
            value={quotationData.CreditTerm ?? "30"} // ✅ Default เป็น 30 days
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded"
            required
          >
            <option value="0">See Condition</option>
            <option value="15">15 days</option>
            <option value="30">30 days</option>
            <option value="45">45 days</option>
          </select>
        </div>

        {/* Remark */}
        <div className="md:col-span-2">
          <label className="block mb-1 text-gray-600">Remark</label>
          <textarea
            name="remark"
            placeholder="Enter remarks"
            value={quotationData.remark || ""}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded h-24"
          />
        </div>
      </div>
    </div>
  );
};

export default CreateQuotationForm;
